import React, { useState, useEffect } from 'react';
import { tradingAPI } from '../../../../services/tradingAPI';
import '../../../../css/trading.css';

const Portfolio = () => {
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [positionsRes, summaryRes] = await Promise.all([
        tradingAPI.getPositions(params),
        tradingAPI.getPositionsSummary(),
      ]);
      // Positions API returns paginated {count, results} — extract the array
      const posData = positionsRes.data;
      setPositions(Array.isArray(posData) ? posData : (posData.results || []));
      // Remap summary field names to what the component expects
      const s = summaryRes.data;
      setSummary({
        total_positions:       s.count            ?? s.total_positions ?? 0,
        total_market_value:    s.total_value       ?? s.total_market_value ?? 0,
        total_unrealized_pl:   s.total_pl          ?? s.total_unrealized_pl ?? 0,
        total_unrealized_pl_pct: s.total_pl_pct    ?? s.total_unrealized_pl_pct ?? 0,
        ...s,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load positions');
      console.error('Positions error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    const num = parseFloat(value) || 0;
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return <div className="loading">Loading positions...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <h3>Error Loading Positions</h3>
        <p>{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="portfolio-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="positions-summary">
          <div className="summary-card">
            <div className="card-label">Total Positions</div>
            <div className="card-value">{summary.total_positions}</div>
          </div>
          <div className="summary-card">
            <div className="card-label">Total Value</div>
            <div className="card-value">{formatCurrency(summary.total_market_value)}</div>
          </div>
          <div className="summary-card">
            <div className="card-label">Unrealized P/L</div>
            <div className={`card-value ${summary.total_unrealized_pl >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(summary.total_unrealized_pl)}
            </div>
            <div className={`card-change ${summary.total_unrealized_pl_pct >= 0 ? 'positive' : 'negative'}`}>
              {formatPercent(summary.total_unrealized_pl_pct)}
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`tab ${statusFilter === 'active' ? 'active' : ''}`}
          onClick={() => setStatusFilter('active')}
        >
          Active
        </button>
        <button
          className={`tab ${statusFilter === 'closed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('closed')}
        >
          Closed
        </button>
        <button
          className={`tab ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All
        </button>
      </div>

      {/* Positions Table */}
      <div className="positions-section">
        {positions && positions.length > 0 ? (
          <div className="positions-table">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Entry Price</th>
                  <th>Current Price</th>
                  <th>Market Value</th>
                  <th>Cost Basis</th>
                  <th>Unrealized P/L</th>
                  <th>Unrealized P/L %</th>
                  <th>Entry Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((position) => (
                  <tr key={position.id}>
                    <td className="symbol">{position.symbol}</td>
                    <td>{position.qty}</td>
                    <td>{formatCurrency(position.entry_price)}</td>
                    <td>{formatCurrency(position.current_price)}</td>
                    <td>{formatCurrency(position.market_value)}</td>
                    <td>{formatCurrency(position.cost_basis)}</td>
                    <td className={position.unrealized_pl >= 0 ? 'positive' : 'negative'}>
                      {formatCurrency(position.unrealized_pl)}
                    </td>
                    <td className={position.unrealized_pl_pct >= 0 ? 'positive' : 'negative'}>
                      {formatPercent(position.unrealized_pl_pct)}
                    </td>
                    <td>{formatDate(position.entry_date)}</td>
                    <td>
                      <span className={`badge badge-${position.status}`}>
                        {position.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <h3>No Positions Found</h3>
            <p>
              {statusFilter === 'active'
                ? 'You have no active positions'
                : statusFilter === 'closed'
                ? 'You have no closed positions'
                : 'No positions to display'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;
